import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';
import { resolveMediaUrl } from '../lib/api-client';
import { makeShortmaxLoader } from '../lib/decrypt/hls-shortmax-loader';
import { decryptMp4CtrToBlobUrl } from '../lib/decrypt/mp4-ctr';
import { decryptMp4EcbToBlobUrl } from '../lib/decrypt/mp4-ecb';
import { telegram } from '../lib/telegram';
import type { DrmInfo, StreamSource, SubtitleTrack } from '../lib/types';

interface Props {
  stream: StreamSource;
  subtitles: SubtitleTrack[];
  drm?: DrmInfo;
  poster?: string | null;
  autoPlay?: boolean;
  onEnded?: () => void;
  /** Detik untuk melanjutkan tontonan. 0 / undefined = mulai dari awal. */
  startAt?: number;
  /** Dipanggil saat posisi berubah — parent yang menentukan kapan disimpan. */
  onProgress?: (seconds: number, duration: number) => void;
  /** Kecepatan pemutaran (1 = normal). */
  playbackRate?: number;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/** Apakah video / wrapper sedang fullscreen (termasuk native iOS video). */
function isFullscreen(video: HTMLVideoElement | null): boolean {
  if (document.fullscreenElement) return true;
  const d = document as any;
  if (d.webkitFullscreenElement) return true;
  // iOS: native video fullscreen tidak lewat Fullscreen API.
  if (video && (video as any).webkitDisplayingFullscreen) return true;
  return false;
}

/**
 * Kunci layar ke landscape saat fullscreen — inilah yang membuat Android
 * berputar sendiri seperti YouTube. iOS tidak mendukung API ini (Safari
 * memutar otomatis lewat fullscreen native-nya), dan desktop mengabaikannya;
 * keduanya aman karena panggilannya kita bungkus.
 */
async function lockLandscape(): Promise<boolean> {
  try {
    const o = (screen as any)?.orientation;
    if (typeof o?.lock !== 'function') return false;
    await o.lock('landscape');
    return true;
  } catch {
    // WebView Telegram di Android kerap MENOLAK ini. Kembalikan false agar
    // pemanggil bisa memakai cadangan rotasi CSS.
    return false;
  }
}

function unlockOrientation(): void {
  try {
    (screen as any)?.orientation?.unlock?.();
  } catch {
    /* tidak didukung — abaikan */
  }
}

/**
 * Masuk fullscreen lintas-browser.
 *
 * Urutan percobaan penting: sebagian WebView Android MENOLAK
 * requestFullscreen pada <div> pembungkus tapi mengizinkannya pada <video>,
 * jadi wrapper dicoba dulu lalu video sebagai cadangan. iOS tidak punya
 * Fullscreen API untuk elemen biasa — hanya video.webkitEnterFullscreen.
 */
async function enterFullscreen(
  wrap: HTMLElement | null,
  video: HTMLVideoElement | null,
): Promise<void> {
  /**
   * Penting: permintaan fullscreen menolak lewat PROMISE, bukan exception.
   * Versi sebelumnya menganggap "fungsinya ada" = berhasil, sehingga saat
   * WebView Android menolak requestFullscreen pada <div> kita berhenti di
   * situ dan tidak pernah mencoba <video> — tombolnya jadi tampak mati.
   * Di sini tiap percobaan benar-benar ditunggu.
   */
  const req = async (el: any): Promise<boolean> => {
    const fn =
      el?.requestFullscreen ??
      el?.webkitRequestFullscreen ??
      el?.mozRequestFullScreen ??
      el?.msRequestFullscreen;
    if (typeof fn !== 'function') return false;
    try {
      await fn.call(el);
      return true;
    } catch {
      return false;
    }
  };

  const vAny = video as any;
  const iosNative = () => {
    if (typeof vAny?.webkitEnterFullscreen === 'function') {
      try {
        vAny.webkitEnterFullscreen();
        return true;
      } catch {
        return false;
      }
    }
    return false;
  };

  // iOS: tidak punya Fullscreen API untuk elemen biasa.
  if (!document.fullscreenEnabled && iosNative()) return;
  if (await req(wrap)) return;
  if (await req(video)) return;
  iosNative();
}

/* eslint-enable @typescript-eslint/no-explicit-any */

/** Method enkripsi MP4 yg di-decrypt client-side (fetch full → blob). */
function mp4EncMethod(stream: StreamSource, drm?: DrmInfo): string | null {
  const method = stream.encryptionMethod ?? drm?.method ?? null;
  if (method === null) return null;
  const m = method.toLowerCase();
  if (m.includes('ctr')) return 'ctr';
  if (m.includes('ecb')) return 'ecb';
  return null;
}

/**
 * Player yang menangani 3 skema QuickPlay:
 *  1. MP4 terenkripsi (Melolo cenc-aes-ctr, DramaBox aes-ecb) — fetch full,
 *     decrypt client-side pakai kunci sah dari API, putar via blob URL.
 *  2. HLS (.m3u8) — HLS.js + custom loader yg auto-decrypt segmen ber-magic
 *     "shortmax" (ShortMax/StoryReel/VibeShort). Segmen biasa pass-through.
 *  3. Plain MP4 / HLS — langsung.
 */
export function VideoPlayer({
  stream,
  subtitles,
  drm,
  poster,
  autoPlay = true,
  onEnded,
  startAt = 0,
  onProgress,
  playbackRate = 1,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const blobRef = useRef<string | null>(null);
  // Callback & posisi awal disimpan di ref supaya perubahannya TIDAK
  // memicu ulang effect pemuatan stream (yang akan me-reset video).
  const startAtRef = useRef(startAt);
  startAtRef.current = startAt;
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;
  const rateRef = useRef(playbackRate);
  rateRef.current = playbackRate;
  /** Menandai seek "lanjutkan" sudah dilakukan untuk stream yang sedang dimuat. */
  const didSeekRef = useRef(false);
  // Ditandai true saat episode selesai & sedang fullscreen — dipakai untuk
  // MASUK fullscreen lagi begitu episode berikutnya mulai main (auto-next
  // tetap fullscreen di iOS/Android).
  const resumeFsRef = useRef(false);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [decrypting, setDecrypting] = useState(false);
  const [fsActive, setFsActive] = useState(false);
  /**
   * Di dalam Telegram, pakai mode layar penuh milik Telegram — WebView-nya
   * di Android kerap menolak Fullscreen API biasa. Di browser/desktop tetap
   * pakai tombol bawaan <video>, jadi tetap SATU tombol di mana pun.
   */
  const [useTgFs] = useState(() => telegram.supportsFullscreen);
  const [tgFsActive, setTgFsActive] = useState(false);
  /**
   * Rasio asli video (lebar/tinggi). Katalog ini didominasi drama pendek
   * TEGAK (9:16); memaksa kotak 16:9 membuat videonya tampil kecil terjepit
   * bar hitam. Default 16:9 sampai metadata terbaca.
   */
  const [aspect, setAspect] = useState(16 / 9);
  const aspectRef = useRef(aspect);
  aspectRef.current = aspect;

  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;
    setFatalError(null);
    setDecrypting(false);
    // Stream baru → boleh melakukan seek "lanjutkan" sekali lagi.
    didSeekRef.current = false;
    let cancelled = false;

    const cleanupHls = () => {
      if (hlsRef.current !== null) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
    const revokeBlob = () => {
      if (blobRef.current !== null) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };
    cleanupHls();
    revokeBlob();

    const primaryUrl = stream.directUrl ?? resolveMediaUrl(stream.url);
    const fallbackUrl = resolveMediaUrl(stream.url);
    const useFallback = stream.directUrl !== undefined && primaryUrl !== fallbackUrl;
    const isHls = primaryUrl.includes('.m3u8');
    const encMethod = mp4EncMethod(stream, drm);
    const key = stream.key ?? drm?.key ?? null;

    // ─── Skema 1: MP4 terenkripsi → fetch penuh + decrypt client-side ───
    if (encMethod !== null && key !== null && !isHls) {
      setDecrypting(true);
      // Fetch selalu via proxy (butuh arraybuffer; direct bisa gagal CORS).
      const fetchUrl = fallbackUrl;
      void (async () => {
        try {
          const res = await fetch(fetchUrl);
          if (!res.ok) throw new Error(`fetch gagal: ${res.status}`);
          const arrbuf = await res.arrayBuffer();
          if (cancelled) return;
          const blobUrl =
            encMethod === 'ctr'
              ? decryptMp4CtrToBlobUrl(arrbuf, key)
              : decryptMp4EcbToBlobUrl(arrbuf, key);
          if (cancelled) {
            URL.revokeObjectURL(blobUrl);
            return;
          }
          blobRef.current = blobUrl;
          video.src = blobUrl;
          setDecrypting(false);
        } catch (err) {
          if (cancelled) return;
          setDecrypting(false);
          const msg = err instanceof Error ? err.message : String(err);
          setFatalError(`Gagal mendekripsi video: ${msg}`);
        }
      })();
      return () => {
        cancelled = true;
        cleanupHls();
        revokeBlob();
      };
    }

    // ─── Skema 2: HLS ───
    const attachHls = (src: string) => {
      const hls = new Hls({
        maxBufferLength: 30,
        maxBufferSize: 30 * 1000 * 1000,
        // Custom loader: decrypt segmen "shortmax" bila ada; lainnya normal.
        fLoader: makeShortmaxLoader(),
      });
      hls.loadSource(src);
      hls.attachMedia(video);
      hlsRef.current = hls;
      return hls;
    };

    // Safari (macOS & iOS) memutar HLS native lebih andal daripada HLS.js
    // (iOS bahkan tidak punya MSE untuk HLS.js). Prefer native bila tersedia.
    const canNativeHls =
      video.canPlayType('application/vnd.apple.mpegurl') !== '' ||
      video.canPlayType('application/x-mpegURL') !== '';

    if (isHls && canNativeHls) {
      // iOS/Safari: HLS native — segmen "shortmax" TIDAK bisa didekripsi
      // di jalur ini (tidak ada custom loader), tapi HLS biasa jalan mulus.
      video.src = primaryUrl;
      let switched = false;
      video.addEventListener('error', () => {
        if (useFallback && !switched) {
          switched = true;
          video.src = fallbackUrl;
        }
      });
    } else if (isHls && Hls.isSupported()) {
      const hls = attachHls(primaryUrl);
      let switched = false;
      hls.on(Hls.Events.ERROR, (_ev, data) => {
        if (!data.fatal) return;
        if (
          data.details === Hls.ErrorDetails.KEY_LOAD_ERROR ||
          data.details === Hls.ErrorDetails.KEY_LOAD_TIMEOUT
        ) {
          hls.destroy();
          hlsRef.current = null;
          setFatalError(
            'Video ini terenkripsi dan tidak dapat diputar di browser. ' +
              'Platform membatasi pemutaran di luar aplikasi resminya.',
          );
          return;
        }
        if (useFallback && !switched && data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          switched = true;
          hls.destroy();
          hlsRef.current = attachHls(fallbackUrl);
          return;
        }
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          setFatalError('Gagal memuat video. Sumber mungkin kedaluwarsa.');
        }
      });
    } else {
      // ─── Skema 3: MP4 / HLS native (Safari) ───
      video.src = primaryUrl;
      let switched = false;
      video.addEventListener('error', () => {
        if (useFallback && !switched) {
          switched = true;
          video.src = fallbackUrl;
          return;
        }
        setFatalError('Gagal memuat video di perangkat ini.');
      });
    }

    // Auto-next tetap fullscreen: begitu episode baru mulai main, kalau
    // episode sebelumnya berakhir dalam mode fullscreen, masuk lagi.
    const onPlaying = () => {
      if (!resumeFsRef.current) return;
      resumeFsRef.current = false;
      // Targetkan <video>, sama seperti tombol fullscreen bawaan — supaya
      // setelah auto-next tampilannya persis seperti sebelum episode ganti.
      void enterFullscreen(null, video);
    };
    video.addEventListener('playing', onPlaying);

    return () => {
      cancelled = true;
      video.removeEventListener('playing', onPlaying);
      cleanupHls();
      revokeBlob();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.url]);

  /**
   * Lanjutkan tontonan + laporkan posisi.
   *
   * Effect ini sengaja DIPISAH dari effect pemuatan stream dan hanya jalan
   * sekali saat mount: element <video> tidak pernah di-unmount (itu yang
   * membuat fullscreen bertahan saat auto-next), jadi listener cukup
   * dipasang satu kali dan membaca nilai terbaru lewat ref.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;

    // Mengganti src me-RESET playbackRate ke 1, jadi harus dipasang ulang
    // setiap stream baru siap — bukan cuma saat user mengubahnya.
    const applyRate = () => {
      try {
        video.playbackRate = rateRef.current;
      } catch {
        // Sebagian codec/browser menolak rate tertentu — abaikan.
      }
    };

    const readAspect = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (w > 0 && h > 0) setAspect(w / h);
    };

    const seekToStart = () => {
      applyRate();
      readAspect();
      if (didSeekRef.current) return;
      const target = startAtRef.current;
      const dur = video.duration;
      if (!Number.isFinite(dur) || dur <= 0) return;
      didSeekRef.current = true;
      // Jangan lanjut kalau posisinya sudah mepet akhir — lebih baik dari awal
      // daripada langsung memicu 'ended'.
      if (target > 2 && target < dur - 10) {
        try {
          video.currentTime = target;
        } catch {
          // Sebagian browser menolak seek sebelum siap — biarkan dari awal.
        }
      }
    };

    const report = () => {
      const dur = video.duration;
      onProgressRef.current?.(
        video.currentTime,
        Number.isFinite(dur) ? dur : 0,
      );
    };

    video.addEventListener('loadedmetadata', seekToStart);
    video.addEventListener('canplay', applyRate);
    video.addEventListener('timeupdate', report);
    video.addEventListener('pause', report);
    return () => {
      video.removeEventListener('loadedmetadata', seekToStart);
      video.removeEventListener('canplay', applyRate);
      video.removeEventListener('timeupdate', report);
      video.removeEventListener('pause', report);
    };
  }, []);

  /**
   * Rotasi otomatis ala YouTube.
   *
   * Dipasang pada EVENT fullscreen, bukan pada tombol kita saja — supaya
   * tetap bekerja walau user menekan tombol fullscreen bawaan browser.
   * iOS memakai event webkitbegin/endfullscreen milik <video>.
   */
  useEffect(() => {
    const video = videoRef.current;
    const sync = () => {
      const fs = isFullscreen(video);
      setFsActive(fs);
      if (!fs) {
        unlockOrientation();
        telegram.unlockOrientation();
        return;
      }
      void (async () => {
        // Video MELINTANG → putar layar ke landscape seperti YouTube.
        // Video TEGAK (drama pendek 9:16) dibiarkan tegak; cukup dikunci
        // supaya tidak ikut berputar saat HP digerakkan.
        if (aspectRef.current > 1) {
          const ok = await lockLandscape();
          if (!ok) telegram.lockOrientation();
        } else {
          telegram.lockOrientation();
        }
      })();
    };

    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    video?.addEventListener('webkitbeginfullscreen', sync);
    video?.addEventListener('webkitendfullscreen', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
      video?.removeEventListener('webkitbeginfullscreen', sync);
      video?.removeEventListener('webkitendfullscreen', sync);
      // Jangan tinggalkan layar terkunci saat pindah halaman.
      unlockOrientation();
      telegram.unlockOrientation();
    };
  }, []);

  /**
   * Langsung layar penuh saat player dibuka.
   *
   * Catatan penting: browser MEWAJIBKAN gestur user untuk Fullscreen API,
   * jadi percobaan tanpa klik biasanya ditolak — itu wajar dan tidak
   * mengganggu apa pun (user tinggal menekan tombol bawaan). Di dalam
   * Telegram dipakai API milik Telegram, yang tidak terikat aturan itu.
   */
  useEffect(() => {
    if (useTgFs) {
      telegram.requestFullscreen();
      return;
    }
    void enterFullscreen(wrapRef.current, videoRef.current);
    // Sekali saja saat player dibuka; ganti episode tidak me-mount ulang.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Mode layar penuh Telegram. Telegram hanya menyembunyikan chrome-nya —
   * membesarkan videonya tetap tugas kita (lihat gaya wrapper di bawah).
   */
  useEffect(() => {
    if (!useTgFs) return;
    const sync = () => {
      const on = telegram.isFullscreen;
      setTgFsActive(on);
      if (on) {
        // Video tegak dibiarkan tegak, cukup dikunci agar tidak ikut
        // berputar; video melintang diminta ke landscape.
        if (aspectRef.current > 1) {
          void lockLandscape().then((ok) => {
            if (!ok) telegram.lockOrientation();
          });
        } else {
          telegram.lockOrientation();
        }
      } else {
        unlockOrientation();
        telegram.unlockOrientation();
      }
    };
    const onFailed = () => setTgFsActive(false);
    telegram.onEvent('fullscreenChanged', sync);
    telegram.onEvent('fullscreenFailed', onFailed);
    return () => {
      telegram.offEvent('fullscreenChanged', sync);
      telegram.offEvent('fullscreenFailed', onFailed);
      // Jangan tinggalkan app dalam mode layar penuh saat pindah halaman.
      if (telegram.isFullscreen) telegram.exitFullscreen();
      telegram.unlockOrientation();
    };
  }, [useTgFs]);

  // Terapkan segera saat user mengubah kecepatan.
  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;
    try {
      video.playbackRate = playbackRate;
    } catch {
      // abaikan — kecepatan tetap di nilai sebelumnya.
    }
  }, [playbackRate]);

  // Saat video ended: tandai kalau sedang fullscreen, LALU beri tahu parent
  // untuk ganti episode. Setelah stream baru main, listener 'playing' di atas
  // masuk fullscreen lagi.
  const handleEnded = () => {
    const v = videoRef.current;
    resumeFsRef.current = isFullscreen(v) || telegram.isFullscreen;
    onEnded?.();
  };



  const needsCors = subtitles.length > 0;
  if (fatalError !== null) {
    return (
      <div className="grid aspect-video w-full place-items-center bg-neutral-900 px-6 text-center">
        <div>
          <p className="text-sm font-medium text-neutral-200">
            Tidak dapat memutar video
          </p>
          <p className="mx-auto mt-2 max-w-md text-xs text-neutral-500">{fatalError}</p>
        </div>
      </div>
    );
  }
  return (
    <div
      ref={wrapRef}
      className={`relative mx-auto bg-black ${
        fsActive || tgFsActive ? 'flex items-center justify-center' : 'w-full'
      }`}
      style={
        tgFsActive
          ? {
              /*
               * Telegram hanya menyembunyikan chrome-nya; posisi elemen tidak
               * ia ubah. Jadi wrapper kita yang dibuat menutupi seluruh
               * layar, dengan video di tengah (object-contain menjaga rasio).
               */
              position: 'fixed',
              inset: 0,
              zIndex: 50,
              width: '100vw',
              height: '100vh',
            }
          : fsActive
          ? { width: '100%', height: '100%' }
          : {
                /*
                 * Ikuti rasio ASLI video. Drama pendek tegak (9:16) jadi
                 * tampil besar, bukan terjepit di kotak 16:9. Dibatasi 70vh
                 * supaya kontrol & daftar episode tetap terlihat tanpa
                 * harus menggulir jauh.
                 */
                aspectRatio: String(aspect),
                maxHeight: '70vh',
                // Batasi lebar ke lebar yang dihasilkan tinggi maksimum,
                // supaya wadahnya menyusut pas ke gambar — tanpa bar hitam
                // di kiri-kanan pada video tegak.
                maxWidth: `calc(70vh * ${aspect})`,
              }
      }
    >
      <video
        ref={videoRef}
        // object-contain: jaga rasio asli, jangan teregang saat fullscreen.
        className="h-full w-full object-contain"
        controls
        /*
         * Sembunyikan tombol fullscreen BAWAAN supaya hanya ada satu tombol.
         * Tombol kita yang dipakai karena ia menangani orientasi dan punya
         * jalur cadangan untuk WebView Android yang menolak fullscreen pada
         * elemen pembungkus. Didukung Chromium (termasuk WebView Telegram);
         * di Safari/iOS atribut ini diabaikan, tapi di sana kedua tombol
         * memang bermuara ke jalur native yang sama.
         */
        /*
         * Tombol fullscreen BAWAAN yang dipakai — satu-satunya di layar,
         * karena kita tidak lagi menggambar tombol sendiri.
         */
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        autoPlay={autoPlay}
        playsInline
        poster={poster ?? undefined}
        onEnded={handleEnded}
        {...(needsCors ? { crossOrigin: 'anonymous' as const } : {})}
      >
        {subtitles.map((s) => (
          <track
            key={s.index}
            kind="subtitles"
            src={resolveMediaUrl(s.url)}
            srcLang={s.language.slice(0, 2)}
            label={s.language}
            default={s.index === 0}
          />
        ))}
      </video>
      {decrypting && (
        <div className="absolute inset-0 grid place-items-center bg-black/70 text-center">
          <div>
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-neutral-600 border-t-white" />
            <p className="mt-3 text-xs text-neutral-300">
              Mendekripsi video…
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
